/**
 * My activity — the signed-in user's own account audit log (web port).
 *
 * Web counterpart of apps/mobile/app/activity.tsx. A self-scoped, read-only
 * feed of the actions a user has taken on their own account (profile edits,
 * farm-detail edits, PIN changes, contributions, etc.). The server scopes GET
 * /me/audit-log to the bearer token's `sub`, so this screen needs no user-id
 * param — the fseAxios interceptor identifies the rows.
 *
 * Each row HUMANISES the raw action code into friendly text. The action-code →
 * i18n-key map (ACTION_LABEL_KEYS) mirrors the mobile `activity.action.<code>`
 * keys (TD-088); an unmapped code (e.g. a newly-added, server-side action)
 * falls back to a generic underscore/dot → spaces transform so the row still
 * renders something legible.
 *
 * Paging: starts at the 50 most-recent rows and APPENDS the next page on
 * "Load more" (accumulated in component state, keyed by offset to dedupe a
 * page that arrives twice). The button hides once we've loaded >= total.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me-activity.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ListItem,
  Stack,
  Chip,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { meControllerGetAuditLog } from '../api/generated/me/me';
import type { AuditLogEntryDto, AuditLogPageDto } from '../api/generated/models';

const PAGE_SIZE = 50;

/**
 * i18n keys for the audit action codes the server currently emits, resolved via
 * t() at render (TD-088). Sourced by grepping `INSERT INTO audit_log` / quoted
 * action strings across services/api/src (me / contributions / farmers / auth /
 * kyc / consent / batches / edit-requests / kanban). The `me.*` and
 * `contribution.recorded` wording mirrors the mobile i18n labels. Anything
 * unmapped falls through to humanizeAction's generic transform, so a new server
 * action stays legible.
 */
const ACTION_LABEL_KEYS: Record<string, string> = {
  // Self-service ("me") actions — what a farmer most often sees here.
  'me.profile.update': 'myActivity.action.me.profile.update',
  'me.farm.update': 'myActivity.action.me.farm.update',
  'me.pin.change': 'myActivity.action.me.pin.change',
  // Contributions.
  'contribution.recorded': 'myActivity.action.contribution.recorded',
  // Account lifecycle / auth.
  'auth.signup': 'myActivity.action.auth.signup',
  'auth.claim': 'myActivity.action.auth.claim',
  'auth.pin_reset': 'myActivity.action.auth.pinReset',
  // Registration & compliance.
  'farmer.registered': 'myActivity.action.farmer.registered',
  'kyc.submitted': 'myActivity.action.kyc.submitted',
  'consent.accepted': 'myActivity.action.consent.accepted',
  // Batches.
  'batch.opened': 'myActivity.action.batch.opened',
  // Edit-request workflow (coop edit approvals).
  'farmer_profile.edit_requested': 'myActivity.action.farmerProfile.editRequested',
  'edit_request.approved': 'myActivity.action.editRequest.approved',
  'edit_request.rejected': 'myActivity.action.editRequest.rejected',
};

/**
 * Map a raw audit action code onto friendly text. Known codes use the curated
 * label above; anything else is generically humanised — split on dots and
 * underscores, join with spaces, and sentence-case the first letter (e.g.
 * `profile_edited` → "Profile edited", `widget.frobbed` → "Widget frobbed").
 */
function humanizeAction(action: string, t: TFunction): string {
  const mappedKey = ACTION_LABEL_KEYS[action];
  if (mappedKey) return t(mappedKey);

  const words = action.replace(/[._]+/g, ' ').trim();
  if (words.length === 0) return action;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A Material Symbols ligature for the leading icon on each audit row — VISUAL
 * ONLY (no copy / behaviour change). Matched against the action prefix so a new
 * server action in a known family still gets a sensible glyph; anything
 * unmatched falls back to a generic history dot.
 */
function actionIcon(action: string): string {
  if (action.startsWith('me.profile') || action.startsWith('auth.')) return 'person';
  if (action.startsWith('me.farm') || action.startsWith('farmer.')) return 'agriculture';
  if (action.startsWith('me.pin')) return 'pin';
  if (action.startsWith('contribution')) return 'inventory_2';
  if (action.startsWith('kyc')) return 'verified_user';
  if (action.startsWith('consent')) return 'gavel';
  if (action.startsWith('batch')) return 'inventory';
  if (action.startsWith('farmer_profile') || action.startsWith('edit_request')) return 'edit_note';
  return 'history';
}

/** Format an ISO-8601 timestamp into a short human date + time, guarded. */
function formatTimestamp(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  return new Date(ts).toLocaleString();
}

export function MyActivityScreen() {
  const { t } = useTranslation();
  // Accumulated rows across the pages we've loaded, plus the offset we want
  // the query to fetch next. `offset` doubles as the slice cursor.
  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState<AuditLogEntryDto[]>([]);
  const [total, setTotal] = useState<number | null>(null);

  const query = useQuery<AuditLogPageDto, FseApiError>({
    queryKey: ['me-audit-log', { limit: PAGE_SIZE, offset }],
    queryFn: ({ signal }) => meControllerGetAuditLog({ limit: PAGE_SIZE, offset }, signal),
  });

  const page = query.data;

  // Merge each fetched page into the accumulated list. Keyed by the page's own
  // offset so a re-render (or a refetch of the same offset) replaces rather
  // than duplicates that slice.
  useEffect(() => {
    if (!page) return;
    setTotal(page.pagination.total);
    setEntries((prev) => {
      const next = prev.slice(0, page.pagination.offset);
      return [...next, ...page.data];
    });
  }, [page]);

  const hasMore = total !== null && entries.length < total;
  const isInitialLoad = query.isPending && entries.length === 0;
  // A page beyond the first is loading when the query is fetching for a
  // non-zero offset we haven't merged yet.
  const isLoadingMore = query.isFetching && offset > 0 && entries.length < (total ?? 0);

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('myActivity.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('myActivity.subtitle')}
          </Text>
        </Box>
      </Box>

      {isInitialLoad ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : query.isError && entries.length === 0 ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              {t('myActivity.retry')}
            </Button>
          }
        >
          {t('myActivity.loadError', { error: query.error.message })}
        </Alert>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('myActivity.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('myActivity.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          <Card>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {entries.map((entry, index) => (
                <ListItem
                  key={`${entry.occurredAt}-${entry.action}-${index}`}
                  leadingIcon={actionIcon(entry.action)}
                  title={
                    <Text variant="title-small" color="text.primary">
                      {humanizeAction(entry.action, t)}
                    </Text>
                  }
                  supportingText={
                    <Text variant="body-small" color="text.secondary">
                      {formatTimestamp(entry.occurredAt)}
                    </Text>
                  }
                  trailing={
                    entry.entityKind ? (
                      <Chip label={entry.entityKind} kind="input" color="neutral" />
                    ) : undefined
                  }
                  noDivider={index === entries.length - 1}
                />
              ))}
            </Box>
          </Card>

          {/* A page-load error after the first page has already rendered. */}
          {query.isError && entries.length > 0 ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => query.refetch()}>
                  {t('myActivity.retry')}
                </Button>
              }
            >
              {t('myActivity.loadMoreError', { error: query.error.message })}
            </Alert>
          ) : null}

          {hasMore ? (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setOffset(entries.length)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('myActivity.loadingMore') : t('myActivity.loadMore')}
              </Button>
            </Box>
          ) : (
            <Text variant="body-small" color="text.secondary">
              {total !== null && total > PAGE_SIZE
                ? t('myActivity.showingAll', { total })
                : t('myActivity.reachedEnd')}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
